import { makeAutoObservable, computed } from 'mobx';
import { createObservableChatGroup, ChatGroupModel, RawChatGroup } from '../models/chatMessage';
import {
  ChatGroupInterface,
  LastMessageInfo,
  MessageDetailsInterface,
  UserInterface
} from '../../interfaces/chatMessageInterface';
import { genericRemove } from '../common';

interface OpenChatSnapshot {
  messages: MessageDetailsInterface[];
  cursor: { before: string; beforeId: string } | null;
  hasMore: boolean;
  updatedAt: string;
  // No change signalled for it since it was left, so reopening needs no request.
  fresh: boolean;
}

interface SeedConversation extends Omit<OpenChatSnapshot, 'updatedAt' | 'fresh'> {
  conversationId: string;
  updatedAt: string | null;
}

// Matches the first-load prefetch (up to 35 conversations).
export const OPEN_CHAT_MEMORY_LIMIT = 35;

export class InputChatGroup {
  [key: string]:
    | string
    | number
    | boolean
    | UserInterface[]
    | MessageDetailsInterface[]
    | { [key: string]: string }
    | LastMessageInfo
    | null
    | undefined
    | ((propsName: string, value: string) => void)
    | ((data: ChatGroupInterface) => void)
    | (() => void);

  constructor() {
    makeAutoObservable(this);
  }

  updateField(propsName: string, value: string) {
    this[propsName] = value;
  }

  setData(data: ChatGroupInterface) {
    Object.keys(data).forEach((key) => {
      this[key] = data[key as keyof ChatGroupInterface];
    });
  }

  clear() {
    Object.keys(this).forEach((key) => {
      delete this[key];
    });
  }
}

export class ChatMessageStore {
  loading: boolean = false;
  chatGroups: ChatGroupModel[] = [];
  pinChatGroups: ChatGroupModel[] = [];
  unpinChatGroups: ChatGroupModel[] = [];
  currentChatGroup: ChatGroupModel | null = null;
  inputChatGroup: InputChatGroup = new InputChatGroup();
  // Cursor paging for the open conversation (older messages loaded on scroll-up).
  openChatCursor: { before: string; beforeId: string } | null = null;
  openChatHasMore: boolean = false;
  openChatLoadingOlder: boolean = false;
  // Server time of the last sync; next refresh fetches only messages changed after it.
  openChatUpdatedAt: string | null = null;
  // First load of the open conversation failed; stop showing the loader.
  openChatLoadFailed: boolean = false;
  // Conversations opened this session, so switching back renders at once (no loader/async gap);
  // the open then fetches only what changed since `updatedAt`. Most recent last.
  openChatMemory: Map<string, OpenChatSnapshot> = new Map();
  // Open conversation was restored from a fresh snapshot (nothing to fetch on open).
  openChatRestoredFresh: boolean = false;
  // A realtime refresh of the open conversation failed; it must re-check when reopened.
  openChatStale: boolean = false;
  // Bumped when loading an older page fails, so the list's scroll trigger re-arms for a retry.
  openChatLoadOlderFailures: number = 0;

  constructor() {
    makeAutoObservable(this, {
      // Mark allChatGroups as computed to optimize performance
      allChatGroups: computed,
      unreadMessagesCount: computed, // add computed registration
      isOpenChatLoading: computed,
      openChatMemory: false
    });
  }

  // Open conversation whose messages haven't loaded yet (sidebar groups carry none). A sync point
  // is set by the first load (API or cache), so until then show loading instead of empty state.
  get isOpenChatLoading() {
    const group = this.currentChatGroup;
    if (!group?.id || group.messages?.length || this.openChatUpdatedAt || this.openChatLoadFailed) return false;
    // Known to have no messages: show the empty state right away.
    return group.lastMessage !== null;
  }

  get allChatGroups() {
    return [...this.pinChatGroups, ...this.unpinChatGroups];
  }

  get unreadMessagesCount(): {
    total: number;
    groupNamesText: string;
    unreadGroups: number;
    unreadGroupIds: string[];
  } {
    const unreadGroupList = this.chatGroups.filter((group) => (group.unreadCount || 0) > 0 && group.hasUnreadMessages);

    const total = unreadGroupList.reduce((sum, group) => sum + (group.unreadCount || 0), 0);
    let groupNamesText = '';
    if (unreadGroupList.length > 0) {
      const firstTwo = unreadGroupList
        .slice(0, 2)
        .map((group) => group.name)
        .join(', ');

      const othersCount = unreadGroupList.length - 2;

      groupNamesText = othersCount > 0 ? `${firstTwo} and ${othersCount} other${othersCount > 1 ? 's' : ''}` : firstTwo;
    }
    return {
      total,
      groupNamesText,
      unreadGroups: unreadGroupList.length,
      // All IDs are returned (no upper bound) so mark-all-read covers every unread group
      unreadGroupIds: unreadGroupList.map((group) => group.id)
    };
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setChatGroups(rawChatGroups: (RawChatGroup | ChatGroupInterface)[]) {
    this.chatGroups = rawChatGroups.map(createObservableChatGroup);
  }

  setPinChatGroups(rawChatGroups: (RawChatGroup | ChatGroupInterface)[]) {
    this.pinChatGroups = rawChatGroups.map(createObservableChatGroup);
  }

  setUnpinChatGroups(rawChatGroups: (RawChatGroup | ChatGroupInterface)[]) {
    this.unpinChatGroups = rawChatGroups.map(createObservableChatGroup);
  }

  setCurrentChatGroup(rawChatGroup: RawChatGroup | ChatGroupInterface | null) {
    if (!rawChatGroup || Object.keys(rawChatGroup).length === 0) {
      this.rememberOpenChat();
      this.currentChatGroup = null;
      this.resetOpenChatSync();
    } else {
      const previous = this.currentChatGroup;
      if (previous?.id !== rawChatGroup.id) this.rememberOpenChat();
      this.currentChatGroup = createObservableChatGroup(rawChatGroup);
      if (previous?.id !== rawChatGroup.id) {
        this.resetOpenChatSync();
        this.restoreOpenChat(rawChatGroup);
      }
      // Lean groups (they carry lastMessage) have no embedded messages; replacing the open
      // conversation with a refreshed copy of itself must keep the messages already loaded.
      if (
        rawChatGroup.lastMessage !== undefined &&
        previous?.id === rawChatGroup.id &&
        !rawChatGroup.messages?.length &&
        previous.messages?.length
      ) {
        this.currentChatGroup.messages = previous.messages;
      }
    }
  }

  setCurrentChatGroupMessages(
    groupId: string,
    messages: MessageDetailsInterface[],
    sync: { cursor: { before: string; beforeId: string } | null; hasMore: boolean; updatedAt: string | null }
  ) {
    if (this.currentChatGroup?.id === groupId) {
      this.currentChatGroup.messages = messages;
      this.openChatStale = false;
      this.openChatCursor = sync.cursor;
      this.openChatHasMore = sync.hasMore;
      this.openChatUpdatedAt = sync.updatedAt;
    }
  }

  // Updates the open conversation's current messages (not a render-time copy, which may be stale).
  updateCurrentChatGroupMessages(
    groupId: string,
    update: (messages: MessageDetailsInterface[]) => MessageDetailsInterface[]
  ) {
    if (this.currentChatGroup?.id === groupId) {
      this.currentChatGroup.messages = update(this.currentChatGroup.messages || []);
    }
  }

  // Keeps the open conversation's loaded messages for an instant switch back.
  rememberOpenChat() {
    const group = this.currentChatGroup;
    if (!group?.id || !this.openChatUpdatedAt) return;
    const syncedUntil = new Date(this.openChatUpdatedAt).getTime();
    this.openChatMemory.delete(group.id);
    this.openChatMemory.set(group.id, {
      // Unconfirmed (optimistic) messages are dropped; real ones come back with the next sync.
      messages: (group.messages || []).filter((message) => new Date(message.createdOn).getTime() <= syncedUntil),
      cursor: this.openChatCursor,
      hasMore: this.openChatHasMore,
      updatedAt: this.openChatUpdatedAt,
      // While open it was kept current by realtime refreshes, unless one failed.
      fresh: !this.openChatStale
    });
    if (this.openChatMemory.size > OPEN_CHAT_MEMORY_LIMIT) {
      const oldest = this.openChatMemory.keys().next().value;
      if (oldest) this.openChatMemory.delete(oldest);
    }
  }

  // Preloads cached conversations (oldest first) so even the first switch to them is instant.
  // Conversations already in memory are newer and kept.
  seedOpenChatMemory(conversations: SeedConversation[]) {
    const seeded = new Map<string, OpenChatSnapshot>();
    conversations.forEach(({ conversationId, messages, cursor, hasMore, updatedAt }) => {
      if (!updatedAt || this.openChatMemory.has(conversationId)) return;
      const syncedUntil = new Date(updatedAt).getTime();
      seeded.set(conversationId, {
        messages: messages.filter((message) => new Date(message.createdOn).getTime() <= syncedUntil),
        cursor,
        hasMore,
        updatedAt,
        // Changes since it was cached are unknown.
        fresh: false
      });
    });
    this.openChatMemory.forEach((snapshot, id) => seeded.set(id, snapshot));
    while (seeded.size > OPEN_CHAT_MEMORY_LIMIT) {
      const oldest = seeded.keys().next().value;
      if (oldest === undefined) break;
      seeded.delete(oldest);
    }
    this.openChatMemory = seeded;
  }

  restoreOpenChat(rawChatGroup: RawChatGroup | ChatGroupInterface) {
    const snapshot = this.openChatMemory.get(rawChatGroup.id);
    if (!snapshot || rawChatGroup.messages?.length || !this.currentChatGroup) return;
    this.currentChatGroup.messages = snapshot.messages;
    this.openChatCursor = snapshot.cursor;
    this.openChatHasMore = snapshot.hasMore;
    this.openChatUpdatedAt = snapshot.updatedAt;
    this.openChatRestoredFresh = snapshot.fresh;
  }

  // Remembered conversations named by a change (all when `ids` is omitted) re-check on open.
  markOpenChatMemoryDirty(ids?: string[]) {
    this.openChatMemory.forEach((snapshot, id) => {
      if (!ids || ids.includes(id)) snapshot.fresh = false;
    });
  }

  markOpenChatStale(groupId: string) {
    if (this.currentChatGroup?.id === groupId) {
      this.openChatStale = true;
      this.openChatRestoredFresh = false;
    }
  }

  resetOpenChatSync() {
    this.openChatCursor = null;
    this.openChatHasMore = false;
    this.openChatLoadingOlder = false;
    this.openChatUpdatedAt = null;
    this.openChatLoadFailed = false;
    this.openChatRestoredFresh = false;
    this.openChatStale = false;
  }

  setOpenChatLoadFailed(groupId: string) {
    if (this.currentChatGroup?.id === groupId) this.openChatLoadFailed = true;
  }

  setOpenChatLoadingOlder(loading: boolean) {
    this.openChatLoadingOlder = loading;
  }

  noteOpenChatLoadOlderFailure() {
    this.openChatLoadOlderFailures += 1;
  }

  setCurrentChatGroupName(rawChatGroup: ChatGroupInterface) {
    this.inputChatGroup.setData(rawChatGroup);
  }

  updateCurrentChatGroupName(propsName: string, value: string) {
    this.inputChatGroup.updateField(propsName, value);
  }

  removeChatGroups(chatGroupIds: string[]) {
    this.chatGroups = genericRemove(this.chatGroups, chatGroupIds);
  }

  removePinChatGroups(chatGroupIds: string[]) {
    this.pinChatGroups = genericRemove(this.pinChatGroups, chatGroupIds);
  }

  removeUnpinChatGroups(chatGroupIds: string[]) {
    this.unpinChatGroups = genericRemove(this.unpinChatGroups, chatGroupIds);
  }

  // Clear chat message related data
  clearChatMessageData() {
    this.loading = false;
    this.chatGroups = [];
    this.pinChatGroups = [];
    this.unpinChatGroups = [];
    this.currentChatGroup = null;
    this.resetOpenChatSync();
    this.openChatMemory.clear();
    this.inputChatGroup.clear();
  }
}
